/**
 * Page-lifecycle persistence: getting the user's work onto disk before the tab goes away.
 *
 * `beforeunload` is NOT a save mechanism. It cannot await IndexedDB, so a write started there is
 * a race the browser is under no obligation to lose, and it does not fire at all on a mobile tab
 * kill or a bfcache eviction. This module therefore treats it as the *last* of four chances:
 *
 *  1. the edit itself — placement, skin and clip commits already save immediately (see
 *     `controllers/index.ts`, `saveNow`); the debounce is only for continuous drags;
 *  2. `visibilitychange` → hidden — the last moment a pending write is still guaranteed to run,
 *     and the one that actually saves the work in practice;
 *  3. `pagehide` / `freeze` — bfcache, tab discard, mobile background kill;
 *  4. `beforeunload` — a best-effort flush plus the browser's own "leave site?" prompt, shown
 *     whenever there is an unsaved clip OR a write that has not landed yet.
 *
 * What cannot be fixed here is that a crash or a `kill -9` takes whatever was still in flight.
 * That is why the pending state is also pushed into the store: the toolbar shows "Saving…", so
 * "is my work safe?" is a question the UI answers rather than one the user has to guess at.
 */

import type { EditorContext } from './context';

/** Anything that owes IndexedDB a write: the project save scheduler, the clip draft autosave. */
export interface PersistenceSink {
  /** Write everything outstanding now, cancelling any debounce. */
  flush(): Promise<void>;
  /** Synchronous: is there queued or in-flight work? */
  isPending(): boolean;
}

/** The `document` slice this module uses — injectable so the wiring is testable in node. */
export interface DocumentLike {
  readonly visibilityState: string;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface PersistenceLifecycleOptions {
  sinks: readonly PersistenceSink[];
  /** Defaults to `window`. */
  target?: EventTarget;
  /** Defaults to `document`. */
  documentTarget?: DocumentLike;
}

export interface PersistenceLifecycleHandle {
  /** Flush every sink. Exposed for tests and for callers that want a hard checkpoint. */
  flushNow(): Promise<void>;
  /** True when any sink still owes a write. */
  isPending(): boolean;
  dispose(): void;
}

/**
 * Fan out `setSaving` over several independent sinks. Two sources writing one boolean would
 * otherwise clear each other's state: the tracker keeps the set of pending sources instead.
 */
export interface PendingSaveTracker {
  /** Returns the `onPendingChange` callback for one named source. */
  track(source: string): (pending: boolean) => void;
  isPending(): boolean;
}

export function createPendingSaveTracker(ctx: EditorContext): PendingSaveTracker {
  const pendingSources = new Set<string>();

  return {
    track(source: string) {
      return (pending: boolean): void => {
        if (pending) pendingSources.add(source);
        else pendingSources.delete(source);
        ctx.store.actions.setSaving(pendingSources.size > 0);
      };
    },
    isPending: () => pendingSources.size > 0,
  };
}

export function installPersistenceLifecycle(
  ctx: EditorContext,
  options: PersistenceLifecycleOptions,
): PersistenceLifecycleHandle {
  const { sinks } = options;
  const target = options.target ?? (typeof window !== 'undefined' ? window : undefined);
  const documentTarget =
    options.documentTarget ??
    (typeof document !== 'undefined' ? (document as unknown as DocumentLike) : undefined);

  const flushNow = async (): Promise<void> => {
    // One rejecting sink must not stop the others from writing.
    await Promise.all(
      sinks.map((sink) =>
        sink.flush().catch((error: unknown) => {
          console.warn('[intch-motion-kit] a pending save could not be flushed', error);
        }),
      ),
    );
  };

  const isPending = (): boolean => sinks.some((sink) => sink.isPending());

  const onVisibilityChange = (): void => {
    if (documentTarget?.visibilityState !== 'hidden') return;
    void flushNow();
  };

  /**
   * `pagehide` and `freeze` are the handlers that fire on the paths `beforeunload` misses: a
   * bfcache navigation, a discarded background tab, an OS-level kill on mobile.
   */
  const onPageHide = (): void => {
    void flushNow();
  };

  const onBeforeUnload = (event: Event): void => {
    // Decide BEFORE flushing: a flush that completes synchronously would otherwise clear the
    // very condition the prompt exists for.
    const unfinished = ctx.store.getState().clipDirty || isPending();
    // Best effort only — see the module comment. The prompt is the actual protection.
    void flushNow();
    if (!unfinished) return;
    event.preventDefault();
    (event as BeforeUnloadEvent).returnValue = '';
  };

  target?.addEventListener('pagehide', onPageHide);
  target?.addEventListener('freeze', onPageHide);
  target?.addEventListener('beforeunload', onBeforeUnload);
  documentTarget?.addEventListener('visibilitychange', onVisibilityChange);

  return {
    flushNow,
    isPending,
    dispose(): void {
      target?.removeEventListener('pagehide', onPageHide);
      target?.removeEventListener('freeze', onPageHide);
      target?.removeEventListener('beforeunload', onBeforeUnload);
      documentTarget?.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
}
